import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Building2,
  ChevronRight,
  CircleUser,
  CreditCardIcon,
  type LucideIcon,
  PhoneIcon,
  Settings2,
  SquareKanban,
  TagIcon,
  UsersIcon,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { useGetHomeQuery } from "@/store/rest_api";
import { MembershipRole } from "@/enum/MembershipRole";
import { MembershipStatus } from "@/enum/MembershipStatus";
import { useSidebarParentMenu } from "@/hooks/useSidebarParentMenu";

interface SettingsNavItem {
  title: string;
  url: string;
  match: string[];
  icon: LucideIcon;
}

const MY_SETTINGS: SettingsNavItem = {
  title: "My settings",
  url: "/my-settings",
  match: ["/my-settings"],
  icon: CircleUser,
};

const ORGANISATION_ITEMS: SettingsNavItem[] = [
  {
    title: "General",
    url: "/manage-organisation/settings",
    match: ["/manage-organisation/settings"],
    icon: Building2,
  },
  {
    title: "Team",
    url: "/manage-organisation/team",
    match: ["/manage-organisation/team"],
    icon: UsersIcon,
  },
  {
    title: "Telephony",
    url: "/manage-organisation/telephony",
    match: ["/manage-organisation/telephony", "/manage-organisation/call-flows"],
    icon: PhoneIcon,
  },
  {
    title: "Tags",
    url: "/manage-organisation/tags",
    match: ["/manage-organisation/tags"],
    icon: TagIcon,
  },
  {
    title: "Deal stages",
    url: "/manage-organisation/deal-stages",
    match: ["/manage-organisation/deal-stages"],
    icon: SquareKanban,
  },
  {
    title: "Billing",
    url: "/manage-organisation/billing/overview",
    match: ["/manage-organisation/billing"],
    icon: CreditCardIcon,
  },
];

export function SidebarSettingsMenu() {
  const { data: user } = useGetHomeQuery();
  const location = useLocation();
  const { isExpanded, setIsExpanded, expandFromIcon } = useSidebarParentMenu(
    [MY_SETTINGS, ...ORGANISATION_ITEMS].some((item) =>
      item.match.some((path) => location.pathname.startsWith(path)),
    ),
  );

  const membership = user?.activeMembership;
  const canManage =
    membership?.status === MembershipStatus.ACTIVE &&
    (membership.role === MembershipRole.OWNER || membership.role === MembershipRole.ADMIN);

  const isRouteActive = (match: string[]) => {
    return match.some((path) => location.pathname.startsWith(path));
  };

  const visibleItems = canManage ? [MY_SETTINGS, ...ORGANISATION_ITEMS] : [MY_SETTINGS];
  const hasActiveChild = visibleItems.some((item) => isRouteActive(item.match));

  const renderItem = (item: SettingsNavItem) => (
    <SidebarMenuSubItem key={item.url}>
      <SidebarMenuSubButton asChild isActive={isRouteActive(item.match)}>
        <Link to={item.url}>
          <item.icon className="h-4 w-4" />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  );

  return (
    <>
      <Separator />
      <SidebarGroup className="pt-0">
        <SidebarMenu>
          <Collapsible asChild open={isExpanded} onOpenChange={setIsExpanded} className="group/collapsible">
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton tooltip="Settings" isActive={hasActiveChild} onClick={expandFromIcon}>
                  <Settings2 size={16} />
                  <span>Settings</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {renderItem(MY_SETTINGS)}

                  {canManage && (
                    <>
                      <div className="px-2 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Manage organisation
                      </div>
                      {ORGANISATION_ITEMS.map(renderItem)}
                    </>
                  )}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        </SidebarMenu>
      </SidebarGroup>
    </>
  );
}
